import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaEye, FaEyeSlash, FaFacebook, FaApple, FaCheckCircle, FaShieldAlt, FaUtensils, FaSpinner } from 'react-icons/fa';
import { FunctionComponent } from 'react';
import { registerUser } from '../lib/api';
import axios from 'axios';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import AppleSignin from 'react-apple-signin-auth';

interface Props {
  setIsLoggedIn: (value: boolean) => void;
}

const SignUp = ({ setIsLoggedIn }: Props) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const FaEyeIcon = FaEye as FunctionComponent<{ className?: string }>;
  const FaEyeSlashIcon = FaEyeSlash as FunctionComponent<{ className?: string }>;
  const FaFacebookIcon = FaFacebook as FunctionComponent<{ className?: string }>;
  const FaAppleIcon = FaApple as FunctionComponent<{ className?: string }>;
  const FaCheckCircleIcon = FaCheckCircle as FunctionComponent<{ className?: string }>;
  const FaShieldAltIcon = FaShieldAlt as FunctionComponent<{ className?: string }>;
  const FaUtensilsIcon = FaUtensils as FunctionComponent<{ className?: string }>;
  const FaSpinnerIcon = FaSpinner as FunctionComponent<{ className?: string }>;

  // Password strength
  const getStrength = (pwd: string): { score: number; label: string; color: string } => {
    if (!pwd) return { score: 0, label: '', color: '' };
    let s = 0;
    if (pwd.length >= 8) s++;
    if (pwd.length >= 12) s++;
    if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) s++;
    if (/\d/.test(pwd)) s++;
    if (/[^A-Za-z0-9]/.test(pwd)) s++;
    if (s <= 2) return { score: s, label: 'Weak', color: 'bg-red-500' };
    if (s <= 3) return { score: s, label: 'Fair', color: 'bg-yellow-500' };
    if (s <= 4) return { score: s, label: 'Good', color: 'bg-blue-500' };
    return { score: s, label: 'Strong', color: 'bg-green-500' };
  };
  const strength = getStrength(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!username.trim() || !email.trim() || !password.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setIsLoading(true);
    try {
      const { jwt, user } = await registerUser({ username, email, password });
      localStorage.setItem('jwt', jwt);
      localStorage.setItem('userId', user.id.toString());
      setIsLoggedIn(true);
      navigate('/profile');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    setIsLoading(true); setError(null);
    try {
      const res = await axios.get('https://www.eatmeatapp.com/api/auth/google/callback', {
        params: { access_token: credentialResponse.credential },
        headers: { 'Content-Type': 'application/json' },
        timeout: 15000,
      });
      if (res.status === 200) {
        const { jwt, user } = res.data;
        localStorage.setItem('jwt', jwt);
        localStorage.setItem('userId', user.id.toString());
        setIsLoggedIn(true);
        navigate('/profile');
      } else setError('Google Sign-Up failed');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Google Sign-Up failed');
    } finally { setIsLoading(false); }
  };

  const handleAppleSuccess = async (response: any) => {
    setIsLoading(true); setError(null);
    try {
      const idToken = response.authorization.id_token;
      if (!idToken) throw new Error('Apple Sign-Up failed: No ID token received');
      const res = await axios.get('https://www.eatmeatapp.com/api/auth/apple/callback', {
        params: { access_token: idToken },
        headers: { 'Content-Type': 'application/json' },
        timeout: 15000,
      });
      if (res.status === 200) {
        const { jwt, user } = res.data;
        localStorage.setItem('jwt', jwt);
        localStorage.setItem('userId', user.id.toString());
        setIsLoggedIn(true);
        navigate('/profile');
      } else setError('Apple Sign-Up failed');
    } catch (err: any) { setError(err.message || 'Apple Sign-Up failed'); }
    finally { setIsLoading(false); }
  };

  const handleSocialLogin = (provider: string) => {
    localStorage.setItem('socialLoginRedirect', '/profile');
    window.location.href = `https://www.eatmeatapp.com/api/connect/${provider}`;
  };

  return (
    <GoogleOAuthProvider clientId="98928347282-p7lkbck0mcffnhmi39eriatmksavbmm9.apps.googleusercontent.com">
      <Helmet>
        <title>Sign Up — EatMeat</title>
        <meta name="description" content="Create your EatMeat account to start your personalized carnivore and ketovore diet journey." />
      </Helmet>

      <div className="min-h-screen bg-gray-50 flex">
        {/* ── LEFT panel (hidden on mobile) ── */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-white border-r border-gray-100 text-gray-800">
          <div className="relative z-10 flex flex-col justify-center p-12 w-full">
            <div className="space-y-8">
              <div>
                <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 rounded-full px-3 py-1 text-xs font-semibold mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Free forever account
                </div>
                <h2 className="text-4xl xl:text-5xl font-extrabold leading-tight mb-4 text-gray-900">
                  Start your <span className="text-primary">carnivore journey</span> today
                </h2>
                <p className="text-gray-500 text-lg max-w-md">Join thousands tracking macros, following meal plans, and transforming their health.</p>
              </div>

              <div className="space-y-3 max-w-md">
                {[
                  { icon: FaUtensilsIcon, text: '500+ premium carnivore & keto recipes', color: 'bg-red-50 text-red-600' },
                  { icon: FaCheckCircleIcon, text: 'Personalized meal plans & macro goals', color: 'bg-green-50 text-green-600' },
                  { icon: FaShieldAltIcon, text: 'Secure OAuth + end-to-end data privacy', color: 'bg-blue-50 text-blue-600' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl p-3">
                    <div className={`w-9 h-9 rounded-lg ${item.color} flex items-center justify-center flex-shrink-0`}>
                      <item.icon className="w-4 h-4" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-5 max-w-md">
                <div className="flex items-center gap-1 mb-2 text-yellow-500 text-lg">★★★★★</div>
                <p className="text-sm text-gray-700 italic mb-2">"Down 18 lbs in 10 weeks. The macro tracker made it effortless."</p>
                <p className="text-xs text-gray-400">— Sarah M., EatMeat member</p>
              </div>

              <div className="flex items-center gap-4 text-sm text-gray-500 pt-2">
                <div className="flex -space-x-2">
                  {['🥩', '🧈', '🥓'].map((e, i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm border-2 border-white shadow-sm">{e}</div>
                  ))}
                </div>
                <span>10,000+ carnivores worldwide</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT panel (form) ── */}
        <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-md">
            <div className="mb-6">
              <Link to="/" className="text-sm text-gray-500 hover:text-primary transition-colors">← Back to Home</Link>
            </div>

            <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8">
              <div className="mb-6">
                <h1 className="text-3xl font-extrabold text-text mb-1">Create your account</h1>
                <p className="text-gray-500 text-sm">Free forever. No credit card required.</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4" role="alert">
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div>
                  <label htmlFor="username" className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Username</label>
                  <input
                    type="text" id="username" value={username} onChange={(e) => setUsername(e.target.value)}
                    placeholder="carnivore_king"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-text focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none transition-all"
                    required autoComplete="username"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Email</label>
                  <input
                    type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-text focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none transition-all"
                    required autoComplete="email"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'} id="password" value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-text focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none transition-all"
                      required autoComplete="new-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-primary rounded-lg transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <FaEyeSlashIcon className="h-4 w-4" /> : <FaEyeIcon className="h-4 w-4" />}
                    </button>
                  </div>
                  {password && (
                    <div className="mt-2">
                      <div className="flex gap-1 mb-1">
                        {[1, 2, 3, 4, 5].map(i => (
                          <div key={i} className={`flex-1 h-1 rounded-full transition-colors ${i <= strength.score ? strength.color : 'bg-gray-200'}`} />
                        ))}
                      </div>
                      <p className="text-[11px] font-medium text-gray-500">Password strength: <span className={`font-bold ${strength.score <= 2 ? 'text-red-600' : strength.score <= 3 ? 'text-yellow-600' : strength.score <= 4 ? 'text-blue-600' : 'text-green-600'}`}>{strength.label}</span></p>
                    </div>
                  )}
                </div>

                <button type="submit" disabled={isLoading}
                  className="w-full bg-gradient-to-r from-primary to-orange-500 text-white font-bold py-3 rounded-xl shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none transition-all flex items-center justify-center"
                  aria-busy={isLoading}>
                  {isLoading ? (<><FaSpinnerIcon className="animate-spin mr-2 h-4 w-4" /> Creating account…</>) : 'Create Free Account'}
                </button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                <div className="relative flex justify-center text-xs"><span className="px-3 bg-white text-gray-400 font-medium uppercase tracking-wider">or sign up with</span></div>
              </div>

              <div className="space-y-2.5">
                <div className="google-btn-wrapper">
                  <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setError('Google sign-up canceled')} size="large" width="360" theme="outline" shape="pill" text="signup_with" />
                </div>

                <AppleSignin
                  authOptions={{
                    clientId: 'com.carivore.lifestyle.eatmeat.auth', scope: 'email name',
                    redirectURI: 'https://www.eatmeatapp.com/api/connect/apple/callback',
                    state: 'state', nonce: 'nonce', usePopup: true,
                  }}
                  uiType="light" className="apple-auth-btn" noDefaultStyle={false}
                  onSuccess={handleAppleSuccess} onError={(err: any) => setError(err.message || 'Apple sign-up canceled')} skipScript={false}
                  render={(props: any) => (
                    <button {...props} disabled={isLoading}
                      className="w-full bg-black text-white font-semibold py-3 rounded-full hover:bg-gray-900 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                      <FaAppleIcon className="h-5 w-5" /> Sign up with Apple
                    </button>
                  )}
                />

                <button type="button" onClick={() => handleSocialLogin('facebook')} disabled={isLoading}
                  className="w-full bg-[#1877F2] text-white font-semibold py-3 rounded-full hover:bg-[#1564d1] disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                  <FaFacebookIcon className="h-5 w-5" /> Sign up with Facebook
                </button>
              </div>

              <p className="mt-6 text-sm text-center text-gray-500">
                Already have an account?{' '}
                <Link to="/login" className="text-primary font-semibold hover:underline">Sign in</Link>
              </p>
            </div>

            <p className="text-center text-xs text-gray-400 mt-6">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="underline hover:text-primary">Terms</Link> and{' '}
              <Link to="/privacy-policy" className="underline hover:text-primary">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default SignUp;
